import { useAddPhoneNumber } from '@/api'
import { cleanUsPhoneWithPlusOne } from '@eigtech/function-utils'
import { HTTPError, useGetErrorResponse } from '@eigtech/ui-shared-api'
import { useAuth0 } from '@eigtech/ui-shared-auth'
import { createForm, FormModal } from '@eigtech/ui-shared-forms/shadcn'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { DialogProps } from '@eigtech/ui/components/dialog'
import { SheetProps } from '@eigtech/ui/components/sheet'
import { toast } from '@eigtech/ui/components/sonner'
import { TriangleAlert } from 'lucide-react'
import { useMemo, useRef } from 'react'
import { z } from 'zod'

type UserSmsModalProps = DialogProps & {
  phoneNumber?: string
}

const formatPhoneNumber = (phoneNumber: string | undefined) => {
  if (!phoneNumber) return undefined

  try {
    const sliced = cleanUsPhoneWithPlusOne(phoneNumber).slice(2)
    // add dashes: 234-567-8901
    return `${sliced.slice(0, 3)}-${sliced.slice(3, 6)}-${sliced.slice(6)}`
  } catch {
    return undefined
  }
}

export function UserSmsModal({ phoneNumber, ...props }: UserSmsModalProps) {
  const form = useForm()
  const actionsRef: Required<SheetProps>['actionsRef'] = useRef(null)

  const {
    mutateAsync: addPhoneNumber,
    isError: isErrorAddPhoneNumber,
    error: addPhoneNumberError,
    reset: resetAddPhoneNumber,
  } = useAddPhoneNumber()

  const addPhoneNumberErrorResponse = useGetErrorResponse(addPhoneNumberError)

  const currentPhoneNumber = useMemo(() => formatPhoneNumber(phoneNumber), [phoneNumber])

  const { user, isLoading: isPendingAuth } = useAuth0()

  const userId = user?.sub ?? ''

  async function handleOnSubmit({ phoneNumber }: UpdatePhoneNumberForm) {
    if (isPendingAuth) {
      toast.warning('Please wait for authentication to complete before adding a phone number')
      return
    }

    resetAddPhoneNumber()

    await addPhoneNumber({ userId, phoneNumber })
    toast.success('Phone number added')
    actionsRef.current?.close()
  }

  return (
    <FormModal
      {...props}
      actionsRef={actionsRef}
      form={form}
      title="Add a phone number for SMS login (optional)"
      onSubmit={handleOnSubmit}
    >
      <MaskedInputField
        defaultValue={currentPhoneNumber ?? undefined}
        label="Phone Number for SMS login (optional)"
        mask="000-000-0000"
        name="phoneNumber"
      />
      {isErrorAddPhoneNumber && (
        <Alert
          variant={
            addPhoneNumberError instanceof HTTPError && addPhoneNumberError.response.status >= 400
              ? 'destructive'
              : 'warning'
          }
        >
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>Could not add phone number</AlertTitle>
            <AlertDescription>{addPhoneNumberErrorResponse}</AlertDescription>
          </AlertContent>
        </Alert>
      )}
    </FormModal>
  )
}

export const UpdatePhoneNumberSchema = z.object({
  phoneNumber: z.string().length(12),
})

const { useForm, MaskedInputField } = createForm(UpdatePhoneNumberSchema, 'updatePhoneNumberForm')

type UpdatePhoneNumberForm = z.infer<typeof UpdatePhoneNumberSchema>
